import { Component, OnInit } from '@angular/core';
import { ApiService, HttpMethod } from '../../../../shared/services/api/api.service';
import { ToasterService } from '../../../../shared/services/toaster/toaster.service';

export interface AuditEntryRow {
  AUDIT_ID?: number;
  USER_ID: number;
  ACTION_TYPE: string;
  RESOURCE_TYPE: string;
  RESOURCE_ID: string;
  DETAILS?: Record<string, unknown> | null;
  TIMESTAMP?: string | Date;
}

@Component({
  selector: 'app-audit-trail-admin',
  template: `
    <div class="audit-trail-admin">
      <div class="inline-form audit-filters">
        <label>
          From
          <input type="datetime-local" name="auditFrom" [(ngModel)]="auditDateFrom" />
        </label>
        <label>
          To
          <input type="datetime-local" name="auditTo" [(ngModel)]="auditDateTo" />
        </label>
        <button type="button" (click)="applyAuditFilters()" [disabled]="isLoadingAudit">
          Apply
        </button>
        <button type="button" (click)="loadAuditTrail()" [disabled]="isLoadingAudit">
          Refresh
        </button>
      </div>

      <p *ngIf="isLoadingAudit">Loading…</p>

      <p *ngIf="!isLoadingAudit && auditEntries.length === 0">
        No audit entries to show.
      </p>

      <table *ngIf="!isLoadingAudit && auditEntries.length > 0" class="data-table">
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>User (ID)</th>
            <th>Action type</th>
            <th>Resource type</th>
            <th>Resource ID</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of auditEntries">
            <td>{{ formatWhen(row.TIMESTAMP) }}</td>
            <td>{{ row.USER_ID }}</td>
            <td>{{ row.ACTION_TYPE }}</td>
            <td>{{ row.RESOURCE_TYPE }}</td>
            <td>{{ row.RESOURCE_ID }}</td>
            <td class="details-cell">{{ formatDetails(row.DETAILS ?? null) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  `,
  styles: [
    `
      :host { display: block; }
      .audit-trail-admin { padding: 0; }
      .inline-form {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        align-items: flex-end;
        margin-bottom: 1rem;
      }
      .inline-form label {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        font-size: 0.875rem;
      }
      .data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.875rem;
      }
      .data-table th,
      .data-table td {
        border: 1px solid #e5e5e5;
        padding: 0.5rem 0.75rem;
        text-align: left;
        vertical-align: top;
      }
      .data-table th {
        background: #fafaf9;
        font-weight: 600;
        color: #6b6b6b;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        font-size: 0.75rem;
      }
      .details-cell {
        max-width: 280px;
        word-break: break-word;
        font-family: monospace;
        font-size: 0.75rem;
      }
    `,
  ],
})
export class AuditTrailAdminComponent implements OnInit {
  auditEntries: AuditEntryRow[] = [];
  auditDateFrom = '';
  auditDateTo = '';
  isLoadingAudit = false;

  constructor(
    private readonly apiService: ApiService,
    private readonly toaster: ToasterService,
  ) {}

  ngOnInit(): void {
    void this.loadAuditTrail();
  }

  async loadAuditTrail(filters?: { dateFrom?: string; dateTo?: string }): Promise<void> {
    this.isLoadingAudit = true;
    const from = filters?.dateFrom ?? this.auditDateFrom;
    const to = filters?.dateTo ?? this.auditDateTo;
    try {
      const params = new URLSearchParams();
      if (from) {
        params.set('dateFrom', from);
      }
      if (to) {
        params.set('dateTo', to);
      }
      const qs = params.toString();
      const path = qs ? `audit-trail?${qs}` : 'audit-trail';
      const data = await this.apiService.call<AuditEntryRow[]>(HttpMethod.GET, path);
      this.auditEntries = Array.isArray(data) ? data : [];
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Failed to load audit trail: ${msg}`, 5000);
      console.error(error);
    } finally {
      this.isLoadingAudit = false;
    }
  }

  applyAuditFilters(): void {
    void this.loadAuditTrail({
      dateFrom: this.auditDateFrom || undefined,
      dateTo: this.auditDateTo || undefined,
    });
  }

  formatDetails(details: Record<string, unknown> | null | undefined): string {
    if (details == null) {
      return '—';
    }
    try {
      return JSON.stringify(details);
    } catch {
      return String(details);
    }
  }

  formatWhen(ts: string | Date | undefined): string {
    if (ts == null) {
      return '—';
    }
    const d = typeof ts === 'string' ? new Date(ts) : ts;
    if (Number.isNaN(d.getTime())) {
      return String(ts);
    }
    return d.toLocaleString();
  }
}
